import { collection, doc, query, where } from 'firebase/firestore'
import { db } from '../lib/firebase'
import { getDoc, getDocs } from '../lib/firestore'
import { isEventActive } from '../lib/formatDate'
import { getNow } from '../lib/clock'
import { byStartDateAsc } from '../lib/utils'
import type { Event, Totem, TotemSlot } from '../types'

// One-shot reads for the totem QR landing, which resolves once and navigates
// away. Network-first on purpose (getDoc/getDocs still fall back to cache
// offline): a stale cached slot list could route a scanner to the wrong festival.

async function loadTotem(groupId: string, totemId: string): Promise<Totem | null> {
  const snap = await getDoc(doc(db, 'groups', groupId, 'totems', totemId))
  return snap.exists() ? ({ id: snap.id, ...snap.data() } as Totem) : null
}

// A totem is reused across festivals, so its slots can span several events. Pick
// the one running now, else the soonest upcoming — that's the festival whoever
// just scanned the code is standing at.
async function resolveLiveEvent(groupId: string, totemId: string): Promise<string | null> {
  const slotSnap = await getDocs(
    query(collection(db, 'totemSlots'), where('groupId', '==', groupId), where('totemId', '==', totemId)),
  )
  const eventIds = [...new Set(slotSnap.docs.map(d => (d.data() as TotemSlot).eventId))]
  if (eventIds.length === 0) return null
  if (eventIds.length === 1) return eventIds[0]

  const now = getNow()
  const events = await Promise.all(eventIds.map(id => getDoc(doc(db, 'events', id))))
  const active = events
    .filter(s => s.exists())
    .map(s => ({ id: s.id, ...s.data() }) as Event)
    .filter(e => isEventActive(e, now))
    .sort(byStartDateAsc)

  return active[0]?.id ?? eventIds[0]
}

const landing = { loadTotem, resolveLiveEvent }

export function useTotemLanding() {
  return landing
}
